// src/pages/projects/ContractsScopePage.jsx
// Contracts & Scope page — manage scope of work lifecycle per project
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Plus, ChevronDown, ChevronRight, Check, X, Trash2,
  Lock, Copy, AlertCircle, CheckCircle, Send, Eye, Pencil, Download, Upload,
} from 'lucide-react';
import { DndContext, PointerSensor, KeyboardSensor, TouchSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BrioPageHeader,
  BrioFrame,
  BrioCard,
  BrioButton,
  BrioBadge,
  BrioSelect,
  BrioInput,
  BrioSection,
  BrioAlert,
} from '../../components/BrioComponents';
import {
  SCOPE_STATUSES,
  SCOPE_STATUS_LABELS,
  SCOPE_STATUS_BADGE_VARIANT,
  validateScopeForConfirmation,
  resolveQuantityTokens,
  resolveNotesTokens,
  INTERIOR_SECTIONS,
  INTERIOR_SECTION_LABELS,
  INTERIOR_SECTION_ORDER,
} from '../../utils/contractScopeHelpers';
import { buildScopeHtml } from '../../scopes/renderScopeHtml';
import { supabase } from '../../supabaseClient';
import SowImportWizard from './SowImportWizard';
import { resolveBrandingImageSrc } from '../../utils/brandingImages';


// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Simple markup renderer: **bold**, *italic*, newlines, and {{token}} resolution
function ScopeSowMarkup({ text, deliverables, className, style: extraStyle }) {
  if (!text) return null;
  let resolved = text;
  if (deliverables && deliverables.length > 0) {
    resolved = resolveQuantityTokens(resolved, deliverables);
  }
  const lines = resolved.split('\n');
  const elements = [];
  lines.forEach((line, li) => {
    if (li > 0) elements.push(<br key={`br-${li}`} />);
    const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    parts.forEach((part, pi) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        elements.push(<strong key={`${li}-${pi}`}>{part.slice(2, -2)}</strong>);
      } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        elements.push(<em key={`${li}-${pi}`}>{part.slice(1, -1)}</em>);
      } else {
        elements.push(<span key={`${li}-${pi}`}>{part}</span>);
      }
    });
  });
  return <div className={className} style={extraStyle}>{elements}</div>;
}

function StatusBadge({ status }) {
  return (
    <BrioBadge variant={SCOPE_STATUS_BADGE_VARIANT[status] || 'neutral'}>
      {SCOPE_STATUS_LABELS[status] || status}
    </BrioBadge>
  );
}

function DeliverableRow({ item, readOnly, onToggle, onRemove, onRename, typeLabel, number, onQuantityChange, allDeliverables }) {
  const resolvedLabel = allDeliverables ? resolveQuantityTokens(item.label, allDeliverables) : item.label;
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(item.label);
  const canRename = !readOnly && typeLabel === 'custom' && !!onRename;

  const startEdit = () => {
    if (!canRename) return;
    setDraftLabel(item.label);
    setEditing(true);
  };
  const commit = () => {
    const trimmed = draftLabel.trim();
    if (trimmed && trimmed !== item.label) onRename(trimmed);
    setEditing(false);
  };

  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2"
      style={{ minHeight: 32, transition: 'background var(--brio-transition-fast)' }}
    >
      {/* Row number */}
      {number != null && (
        <span className="flex-shrink-0" style={{ width: 20, textAlign: 'right', fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
          {number}.
        </span>
      )}
      <input
        type="checkbox"
        checked={item.included}
        disabled={readOnly}
        onChange={onToggle}
        className="brio-checkbox"
      />
      {editing ? (
        <input
          type="text"
          autoFocus
          value={draftLabel}
          onChange={e => setDraftLabel(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') { setDraftLabel(item.label); setEditing(false); }
          }}
          className="brio-input flex-1"
          style={{ fontSize: 'var(--brio-font-size-base)', padding: '2px 6px' }}
        />
      ) : (
        <span
          className="flex-1"
          onClick={startEdit}
          style={{
            fontSize: 'var(--brio-font-size-base)',
            color: item.included ? 'var(--brio-text-primary)' : 'var(--brio-text-muted)',
            textDecoration: item.included ? 'none' : 'line-through',
            cursor: canRename ? 'text' : 'default',
          }}
          title={canRename ? 'Click to rename' : undefined}
        >
          {resolvedLabel}
        </span>
      )}
      {/* Editable quantity */}
      {item.quantity !== undefined && (
        readOnly ? (
          <span className="flex-shrink-0 px-1.5 py-0.5" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', backgroundColor: 'var(--brio-grey-light)' }}>
            ({item.quantity})
          </span>
        ) : (
          <input
            type="text"
            value={item.quantity}
            onChange={e => onQuantityChange && onQuantityChange(e.target.value)}
            className="brio-modal-input flex-shrink-0 text-center"
            style={{ width: 48, padding: '2px 4px', fontSize: 'var(--brio-font-size-s)' }}
            title={item.quantityToken ? `Linked to {{${item.quantityToken}}} in introduction` : 'Quantity'}
          />
        )
      )}
      <span
        className="px-1.5 py-0.5"
        style={{
          fontSize: 'var(--brio-font-size-s)',
          backgroundColor:
            typeLabel === 'standard' ? 'var(--brio-grey-light)' :
            typeLabel === 'optional' ? 'var(--brio-gold-light)' : 'var(--brio-teal-light)',
          color:
            typeLabel === 'standard' ? 'var(--brio-text-secondary)' :
            typeLabel === 'optional' ? 'var(--brio-gold)' : 'var(--brio-teal)',
        }}
      >
        {typeLabel}
      </span>
      {item.type === 'custom' && !readOnly && (
        <button
          type="button"
          onClick={onRemove}
          className="brio-icon-btn brio-icon-btn--small brio-icon-btn--delete flex-shrink-0"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

function StageCard({
  stage,
  readOnly,
  expanded,
  onToggleExpand,
  onToggleSelected,
  onToggleDeliverable,
  onAddCustom,
  onRemoveCustom,
  onRenameCustom,
  onMarkDescriptionReviewed,
  onUpdateDescription,
  onUpdateStage,
  dragHandleProps,
}) {
  const [customInput, setCustomInput] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [descDraft, setDescDraft] = useState(stage.description);
  const [editingIntroduction, setEditingIntroduction] = useState(false);
  const [introDraft, setIntroDraft] = useState(stage.introduction || '');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraftLocal, setNotesDraftLocal] = useState(stage.notes || '');
  const [editingCustomId, setEditingCustomId] = useState(null);
  const [customLabelDraft, setCustomLabelDraft] = useState('');

  const allDeliverables = [
    ...stage.deliverables.standard,
    ...stage.deliverables.optional,
    ...stage.deliverables.custom,
  ];
  const includedCount = allDeliverables.filter(d => d.included).length;

  const handleAddCustom = () => {
    const trimmed = customInput.trim();
    if (!trimmed) return;
    onAddCustom(trimmed);
    setCustomInput('');
  };

  const handleSaveDescription = () => {
    onUpdateDescription(descDraft);
    setEditingDescription(false);
  };

  const handleQuantityChange = (delivType, delivId, newQuantity) => {
    if (!onUpdateStage) return;
    const updatedList = stage.deliverables[delivType].map(d =>
      d.id === delivId ? { ...d, quantity: newQuantity } : d
    );
    onUpdateStage({ deliverables: { ...stage.deliverables, [delivType]: updatedList } });
  };

  return (
    <div
      className="overflow-hidden"
      style={{
        border: `1px solid ${stage.selected ? 'var(--brio-gold)' : 'var(--brio-border)'}`,
        borderRadius: 0,
        boxShadow: stage.selected ? '0 0 0 1px var(--brio-gold)' : 'none',
        opacity: stage.selected ? 1 : 0.7,
        transition: 'all var(--brio-transition-default)',
      }}
    >
      {/* Stage header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        style={{
          backgroundColor: stage.selected ? 'var(--brio-gold-light)' : 'var(--brio-grey-light)',
          touchAction: dragHandleProps && !readOnly ? 'none' : undefined,
        }}
        onClick={onToggleExpand}
        {...(dragHandleProps && !readOnly ? dragHandleProps : {})}
      >
        {/* Selection checkbox */}
        <input
          type="checkbox"
          checked={stage.selected}
          disabled={readOnly}
          onChange={onToggleSelected}
          onClick={e => e.stopPropagation()}
          className="brio-checkbox"
        />

        {/* Expand icon */}
        <span className="flex-shrink-0" style={{ color: 'var(--brio-text-muted)' }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>

        {/* Stage name */}
        <div className="flex-1 min-w-0">
          <span className="font-semibold" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-primary)' }}>
            {stage.name}
          </span>
          {stage.fullName && stage.fullName !== stage.name && (
            <span className="ml-2" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>{stage.fullName}</span>
          )}
        </div>

        {/* Status indicators */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {stage.selected && stage.descriptionReviewed && (
            <span title="Description reviewed" style={{ color: 'var(--brio-green)' }}><CheckCircle size={14} /></span>
          )}
          {stage.selected && (
            <span style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
              {includedCount}/{allDeliverables.length} deliverables
            </span>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 py-3" style={{ borderTop: '1px solid var(--brio-border)' }}>
          {/* Description */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold uppercase tracking-wide" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                Stage Description
              </span>
              <div className="flex items-center gap-2">
                {!readOnly && !editingDescription && (
                  <button
                    type="button"
                    onClick={() => { setDescDraft(stage.description); setEditingDescription(true); }}
                    className="underline"
                    style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-gold)' }}
                  >
                    Edit
                  </button>
                )}
                {stage.selected && !readOnly && (
                  <button
                    type="button"
                    onClick={onMarkDescriptionReviewed}
                    className="flex items-center gap-1 px-2 py-0.5"
                    style={{
                      fontSize: 'var(--brio-font-size-s)',
                      backgroundColor: stage.descriptionReviewed ? 'var(--brio-green-light)' : 'var(--brio-grey-light)',
                      color: stage.descriptionReviewed ? 'var(--brio-green)' : 'var(--brio-text-secondary)',
                      transition: 'all var(--brio-transition-default)',
                    }}
                  >
                    <CheckCircle size={12} />
                    {stage.descriptionReviewed ? 'Reviewed' : 'Mark as reviewed'}
                  </button>
                )}
              </div>
            </div>
            {editingDescription ? (
              <div>
                <textarea
                  className="brio-modal-input"
                  style={{ minHeight: 80 }}
                  value={descDraft}
                  onChange={e => setDescDraft(e.target.value)}
                />
                <div className="flex gap-2 mt-1">
                  <BrioButton variant="primary" onClick={handleSaveDescription}>Save</BrioButton>
                  <BrioButton variant="ghost" onClick={() => setEditingDescription(false)}>Cancel</BrioButton>
                </div>
              </div>
            ) : (
              <p className="leading-relaxed" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}>{stage.description}</p>
            )}
          </div>

          {/* Introduction text with markup */}
          {(stage.introduction || !readOnly) && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold uppercase tracking-wide" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                  Introduction
                </span>
                {!readOnly && !editingIntroduction && (
                  <button
                    type="button"
                    onClick={() => { setIntroDraft(stage.introduction || ''); setEditingIntroduction(true); }}
                    className="underline"
                    style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-gold)' }}
                  >
                    {stage.introduction ? 'Edit' : 'Add'}
                  </button>
                )}
              </div>
              {editingIntroduction ? (
                <div>
                  <textarea
                    value={introDraft}
                    onChange={e => setIntroDraft(e.target.value)}
                    rows={Math.max(4, Math.min(20, (introDraft.match(/\n/g) || []).length + 2))}
                    className="brio-input"
                    style={{ width: '100%', fontSize: 'var(--brio-font-size-base)', padding: 8, lineHeight: 1.5 }}
                    placeholder="Optional introduction shown above the deliverables. Use **text** for bold sub-headers."
                  />
                  <div className="flex gap-2 mt-1">
                    <BrioButton variant="primary" onClick={() => { onUpdateStage({ introduction: introDraft }); setEditingIntroduction(false); }}>Save</BrioButton>
                    <BrioButton variant="ghost" onClick={() => { setIntroDraft(stage.introduction || ''); setEditingIntroduction(false); }}>Cancel</BrioButton>
                  </div>
                </div>
              ) : stage.introduction ? (
                <ScopeSowMarkup
                  text={stage.introduction}
                  deliverables={[...stage.deliverables.standard, ...stage.deliverables.optional]}
                  className="leading-relaxed"
                  style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-primary)', fontWeight: 300 }}
                />
              ) : (
                <p style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>
                  No introduction. Click "Add" to write one.
                </p>
              )}
            </div>
          )}

          {/* Deliverables — numbered */}
            <div>
              <span className="font-semibold uppercase tracking-wide mb-2 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                Deliverables
              </span>

              {/* Standard */}
              {stage.deliverables.standard.map((d, idx) => (
                <DeliverableRow
                  key={d.id}
                  item={d}
                  number={idx + 1}
                  typeLabel="standard"
                  readOnly={readOnly}
                  onToggle={() => onToggleDeliverable('standard', d.id)}
                  onRemove={() => {}}
                  onQuantityChange={(val) => handleQuantityChange('standard', d.id, val)}
                  allDeliverables={allDeliverables}
                />
              ))}

              {/* Optional */}
              {stage.deliverables.optional.length > 0 && (
                <>
                  <div className="my-2" style={{ borderTop: '1px solid var(--brio-border)' }} />
                  {stage.deliverables.optional.map((d, idx) => (
                    <DeliverableRow
                      key={d.id}
                      item={d}
                      number={stage.deliverables.standard.length + idx + 1}
                      typeLabel="optional"
                      readOnly={readOnly}
                      onToggle={() => onToggleDeliverable('optional', d.id)}
                      onRemove={() => {}}
                      onQuantityChange={(val) => handleQuantityChange('optional', d.id, val)}
                      allDeliverables={allDeliverables}
                    />
                  ))}
                </>
              )}

              {/* Custom */}
              {stage.deliverables.custom.length > 0 && (
                <>
                  <div className="my-2" style={{ borderTop: '1px solid var(--brio-border)' }} />
                  {stage.deliverables.custom.map((d, idx) => (
                    <DeliverableRow
                      key={d.id}
                      item={d}
                      number={stage.deliverables.standard.length + stage.deliverables.optional.length + idx + 1}
                      typeLabel="custom"
                      readOnly={readOnly}
                      onToggle={() => onToggleDeliverable('custom', d.id)}
                      onRemove={() => onRemoveCustom(d.id)}
                      onRename={(label) => onRenameCustom && onRenameCustom(d.id, label)}
                    />
                  ))}
                </>
              )}

              {/* Add custom */}
              {!readOnly && (
                <div className="flex gap-2 mt-3">
                  <BrioInput
                    placeholder="Add custom deliverable…"
                    value={customInput}
                    onChange={e => setCustomInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddCustom(); }}
                    className="flex-1"
                    style={{ fontSize: 'var(--brio-font-size-s)' }}
                  />
                  <BrioButton
                    variant="ghost"
                    onClick={handleAddCustom}
                    disabled={!customInput.trim()}
                  >
                    <Plus size={14} />
                  </BrioButton>
                </div>
              )}
            </div>

          {/* Notes with markup */}
          {(stage.notes || !readOnly) && (
            <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--brio-border)' }}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold uppercase tracking-wide" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                  Notes
                </span>
                {!readOnly && !editingNotes && (
                  <button
                    type="button"
                    onClick={() => { setNotesDraftLocal(stage.notes || ''); setEditingNotes(true); }}
                    className="underline"
                    style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-gold)' }}
                  >
                    {stage.notes ? 'Edit' : 'Add'}
                  </button>
                )}
              </div>
              {editingNotes ? (
                <div>
                  <textarea
                    value={notesDraftLocal}
                    onChange={e => setNotesDraftLocal(e.target.value)}
                    rows={Math.max(3, Math.min(15, (notesDraftLocal.match(/\n/g) || []).length + 2))}
                    className="brio-input"
                    style={{ width: '100%', fontSize: 'var(--brio-font-size-base)', padding: 8, lineHeight: 1.5 }}
                    placeholder="Notes shown after the deliverables (e.g. revision allowance). Use {{revisions}} and {{meetings}} tokens to link to the counters below."
                  />
                  <div className="flex gap-2 mt-1">
                    <BrioButton variant="primary" onClick={() => { onUpdateStage({ notes: notesDraftLocal }); setEditingNotes(false); }}>Save</BrioButton>
                    <BrioButton variant="ghost" onClick={() => { setNotesDraftLocal(stage.notes || ''); setEditingNotes(false); }}>Cancel</BrioButton>
                  </div>
                </div>
              ) : stage.notes ? (
                <ScopeSowMarkup
                  text={resolveNotesTokens(stage.notes, stage)}
                  deliverables={[...stage.deliverables.standard, ...stage.deliverables.optional]}
                  className="leading-relaxed italic"
                  style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}
                />
              ) : (
                <p style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>
                  No notes. Click "Add" to write some.
                </p>
              )}
              {/* Editable revision / meeting counts (design stages only) */}
              {stage.notesRevisions !== undefined && !readOnly && (
                <div className="flex gap-4 mt-2 items-center">
                  <div className="flex items-center gap-1">
                    <span style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>Revisions:</span>
                    <input
                      type="text"
                      value={stage.notesRevisions}
                      onChange={e => onUpdateStage({ notesRevisions: e.target.value })}
                      className="brio-modal-input text-center"
                      style={{ width: 48, padding: '2px 4px', fontSize: 'var(--brio-font-size-s)' }}
                      title="Number of revisions — linked to {{revisions}} in notes"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>Meetings:</span>
                    <input
                      type="text"
                      value={stage.notesMeetings}
                      onChange={e => onUpdateStage({ notesMeetings: e.target.value })}
                      className="brio-modal-input text-center"
                      style={{ width: 48, padding: '2px 4px', fontSize: 'var(--brio-font-size-s)' }}
                      title="Number of meetings — linked to {{meetings}} in notes"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
}


// ============================================================================
// SORTABLE STAGE CARD WRAPPER
// ============================================================================

function SortableStageCard({ id, onUpdateStage, ...props }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <StageCard {...props} onUpdateStage={onUpdateStage} dragHandleProps={listeners} />
    </div>
  );
}


// ============================================================================
// SUMMARY OF INTERIOR DELIVERABLES (top-level scope section)
// ============================================================================
// Mirrors a stage card: selection checkbox in the header, collapsible
// body with an editable introduction, and an editable list of items
// (rename/add/remove). Included items render in the saved scope's
// PDF as the "Summary of Interior Deliverables" list.

function InteriorDeliverablesPanel({ scope, readOnly, expanded, onToggleExpand, onUpdateScope }) {
  const items = scope.interiorDeliverables || [];
  const warmShellSelected = !!scope.interiorWarmShellSelected;
  const ffeSelected = !!scope.interiorFfeSelected;
  const anySectionSelected = warmShellSelected || ffeSelected;
  const sectionSelectedMap = {
    [INTERIOR_SECTIONS.WARM_SHELL]: warmShellSelected,
    [INTERIOR_SECTIONS.FFE]: ffeSelected,
  };
  const introText = scope.interiorIntroduction || '';

  const [editingIntroduction, setEditingIntroduction] = useState(false);
  const [introDraft, setIntroDraft] = useState(introText);
  const [editingItemId, setEditingItemId] = useState(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [addInputs, setAddInputs] = useState({});

  const canEdit = !readOnly;

  const updateItems = (next) => onUpdateScope({ interiorDeliverables: next });
  const handleToggleSection = (sectionKey) => {
    if (sectionKey === INTERIOR_SECTIONS.WARM_SHELL) {
      onUpdateScope({ interiorWarmShellSelected: !warmShellSelected });
    } else {
      onUpdateScope({ interiorFfeSelected: !ffeSelected });
    }
  };
  const handleToggleItem = (id) => updateItems(items.map(d => d.id === id ? { ...d, included: !d.included } : d));
  const handleRenameItem = (id, label) => updateItems(items.map(d => d.id === id ? { ...d, label } : d));
  const handleRemoveItem = (id) => updateItems(items.filter(d => d.id !== id));
  const handleMoveItemSection = (id, toSection) => updateItems(items.map(d => d.id === id ? { ...d, section: toSection } : d));
  const handleAddItem = (sectionKey) => {
    const trimmed = (addInputs[sectionKey] || '').trim();
    if (!trimmed) return;
    const id = `intd-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    updateItems([...items, { id, label: trimmed, included: true, section: sectionKey }]);
    setAddInputs(prev => ({ ...prev, [sectionKey]: '' }));
  };

  // Items missing a section default to warm-shell (legacy data).
  const itemsBySection = INTERIOR_SECTION_ORDER.reduce((acc, key) => {
    acc[key] = items.filter(d => (d.section || INTERIOR_SECTIONS.WARM_SHELL) === key);
    return acc;
  }, {});
  const includedCount = items.filter(d => {
    const sect = d.section || INTERIOR_SECTIONS.WARM_SHELL;
    return d.included && sectionSelectedMap[sect];
  }).length;
  const eligibleCount = items.filter(d => sectionSelectedMap[d.section || INTERIOR_SECTIONS.WARM_SHELL]).length;

  return (
    <div
      className="mb-4 overflow-hidden"
      style={{
        border: `1px solid ${anySectionSelected ? 'var(--brio-gold)' : 'var(--brio-border)'}`,
        boxShadow: anySectionSelected ? '0 0 0 1px var(--brio-gold)' : 'none',
        opacity: anySectionSelected ? 1 : 0.7,
        transition: 'all var(--brio-transition-default)',
      }}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        style={{ backgroundColor: anySectionSelected ? 'var(--brio-gold-light)' : 'var(--brio-grey-light)' }}
        onClick={onToggleExpand}
      >
        <span className="flex-shrink-0" style={{ color: 'var(--brio-text-muted)' }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        <span className="font-semibold flex-1" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-primary)' }}>
          Summary of Interior Deliverables
        </span>
        <label className="flex items-center gap-1 cursor-pointer" onClick={e => e.stopPropagation()} style={{ fontSize: 'var(--brio-font-size-s)' }}>
          <input
            type="checkbox"
            checked={warmShellSelected}
            disabled={readOnly}
            onChange={() => handleToggleSection(INTERIOR_SECTIONS.WARM_SHELL)}
            className="brio-checkbox"
          />
          <span style={{ color: 'var(--brio-text-primary)' }}>{INTERIOR_SECTION_LABELS[INTERIOR_SECTIONS.WARM_SHELL]}</span>
        </label>
        <label className="flex items-center gap-1 cursor-pointer" onClick={e => e.stopPropagation()} style={{ fontSize: 'var(--brio-font-size-s)' }}>
          <input
            type="checkbox"
            checked={ffeSelected}
            disabled={readOnly}
            onChange={() => handleToggleSection(INTERIOR_SECTIONS.FFE)}
            className="brio-checkbox"
          />
          <span style={{ color: 'var(--brio-text-primary)' }}>{INTERIOR_SECTION_LABELS[INTERIOR_SECTIONS.FFE]}</span>
        </label>
        <span style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
          {includedCount} of {eligibleCount}
        </span>
      </div>

      {expanded && (
        <div className="px-4 py-3" style={{ borderTop: '1px solid var(--brio-border)' }}>
          {/* Introduction editor */}
          {(introText || canEdit) && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold uppercase tracking-wide" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                  Introduction
                </span>
                {canEdit && !editingIntroduction && (
                  <button
                    type="button"
                    onClick={() => { setIntroDraft(introText); setEditingIntroduction(true); }}
                    className="underline"
                    style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-gold)' }}
                  >
                    {introText ? 'Edit' : 'Add'}
                  </button>
                )}
              </div>
              {editingIntroduction ? (
                <div>
                  <textarea
                    value={introDraft}
                    onChange={e => setIntroDraft(e.target.value)}
                    rows={Math.max(3, Math.min(15, (introDraft.match(/\n/g) || []).length + 2))}
                    className="brio-input"
                    style={{ width: '100%', fontSize: 'var(--brio-font-size-base)', padding: 8, lineHeight: 1.5 }}
                    placeholder="Optional introduction shown above the interior deliverables list. Use **text** for bold sub-headers."
                  />
                  <div className="flex gap-2 mt-1">
                    <BrioButton variant="primary" onClick={() => { onUpdateScope({ interiorIntroduction: introDraft }); setEditingIntroduction(false); }}>Save</BrioButton>
                    <BrioButton variant="ghost" onClick={() => { setIntroDraft(introText); setEditingIntroduction(false); }}>Cancel</BrioButton>
                  </div>
                </div>
              ) : introText ? (
                <ScopeSowMarkup
                  text={introText}
                  className="leading-relaxed"
                  style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-primary)', fontWeight: 300 }}
                />
              ) : (
                <p style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>
                  No introduction. Click "Add" to write one.
                </p>
              )}
            </div>
          )}

          {/* Item list, grouped by section */}
          {items.length === 0 && !canEdit && (
            <p style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>
              No interior deliverables.
            </p>
          )}
          {INTERIOR_SECTION_ORDER.map(sectionKey => {
            const sectionItems = itemsBySection[sectionKey] || [];
            const sectionOn = sectionSelectedMap[sectionKey];
            const otherSectionKey = sectionKey === INTERIOR_SECTIONS.WARM_SHELL ? INTERIOR_SECTIONS.FFE : INTERIOR_SECTIONS.WARM_SHELL;
            const addValue = addInputs[sectionKey] || '';
            if (sectionItems.length === 0 && !canEdit) return null;
            return (
              <div key={sectionKey} className="mb-3">
                <div className="flex items-center gap-2 mb-1" style={{ fontSize: 'var(--brio-font-size-s)' }}>
                  <span
                    className="font-semibold uppercase tracking-wide"
                    style={{
                      color: sectionOn ? 'var(--brio-text-primary)' : 'var(--brio-text-muted)',
                      fontSize: 11,
                    }}
                  >
                    {INTERIOR_SECTION_LABELS[sectionKey]}
                  </span>
                  <span style={{ color: 'var(--brio-text-muted)' }}>
                    ({sectionItems.filter(d => d.included).length}/{sectionItems.length})
                  </span>
                  {!sectionOn && (
                    <span style={{ color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>
                      — section not selected
                    </span>
                  )}
                </div>
                {sectionItems.length === 0 ? (
                  <p className="ml-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>
                    No items in this section yet.
                  </p>
                ) : (
                  sectionItems.map((item, idx) => {
                    const isEditing = editingItemId === item.id;
                    const commit = () => {
                      const trimmed = labelDraft.trim();
                      if (trimmed && trimmed !== item.label) handleRenameItem(item.id, trimmed);
                      setEditingItemId(null);
                    };
                    return (
                      <div key={item.id} className="flex items-center gap-2 py-0.5" style={{ fontSize: 'var(--brio-font-size-base)' }}>
                        <span className="flex-shrink-0" style={{ width: 20, textAlign: 'right', fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
                          {idx + 1}.
                        </span>
                        <input
                          type="checkbox"
                          checked={item.included}
                          disabled={readOnly || !sectionOn}
                          onChange={() => handleToggleItem(item.id)}
                          className="brio-checkbox"
                        />
                        {isEditing ? (
                          <input
                            type="text"
                            autoFocus
                            value={labelDraft}
                            onChange={e => setLabelDraft(e.target.value)}
                            onBlur={commit}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commit();
                              else if (e.key === 'Escape') setEditingItemId(null);
                            }}
                            className="brio-input flex-1"
                            style={{ fontSize: 'var(--brio-font-size-base)', padding: '2px 6px' }}
                          />
                        ) : (
                          <span
                            onClick={() => { if (canEdit) { setLabelDraft(item.label); setEditingItemId(item.id); } }}
                            className="flex-1"
                            style={{
                              color: item.included && sectionOn ? 'var(--brio-text-primary)' : 'var(--brio-text-muted)',
                              textDecoration: item.included ? 'none' : 'line-through',
                              cursor: canEdit ? 'text' : 'default',
                            }}
                            title={canEdit ? 'Click to rename' : undefined}
                          >
                            {item.label}
                          </span>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => handleMoveItemSection(item.id, otherSectionKey)}
                            className="brio-icon-btn brio-icon-btn--small flex-shrink-0"
                            title={`Move to ${INTERIOR_SECTION_LABELS[otherSectionKey]}`}
                            style={{ fontSize: 10, color: 'var(--brio-gold)', padding: '2px 6px' }}
                          >
                            → {INTERIOR_SECTION_LABELS[otherSectionKey]}
                          </button>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(item.id)}
                            className="brio-icon-btn brio-icon-btn--small brio-icon-btn--delete flex-shrink-0"
                            aria-label="Remove"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
                {canEdit && (
                  <div className="flex gap-2 mt-2">
                    <BrioInput
                      placeholder={`Add ${INTERIOR_SECTION_LABELS[sectionKey]} deliverable…`}
                      value={addValue}
                      onChange={e => setAddInputs(prev => ({ ...prev, [sectionKey]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') handleAddItem(sectionKey); }}
                      className="flex-1"
                      style={{ fontSize: 'var(--brio-font-size-s)' }}
                    />
                    <BrioButton variant="ghost" onClick={() => handleAddItem(sectionKey)} disabled={!addValue.trim()}>
                      <Plus size={14} />
                    </BrioButton>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// SCOPE VERSION LIST
// ============================================================================

function ScopeVersionList({ scopes, activeScopeId, onSelect, onCreateNew, onDelete, isAdmin }) {
  if (scopes.length === 0) return null;

  // Sort: oldest first so version numbers ascend left-to-right.
  const sorted = [...scopes].sort((a, b) => a.version - b.version);

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold uppercase tracking-wide" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
          Versions
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {sorted.map(s => {
          const isActive = s.id === activeScopeId;
          // Block deletion of the live confirmed version — that's the
          // authoritative scope. Drafts and archived versions can be
          // pruned freely (gated to admins).
          const isActiveConfirmed = s.isActiveVersion && s.status === SCOPE_STATUSES.CONFIRMED;
          const canDelete = !!onDelete && !!isAdmin && !isActiveConfirmed;
          return (
            <div
              key={s.id}
              className="flex items-stretch"
              style={{
                border: `1px solid ${isActive ? 'var(--brio-gold)' : 'var(--brio-border)'}`,
                backgroundColor: isActive ? 'var(--brio-gold-light)' : 'var(--brio-white)',
                transition: 'all var(--brio-transition-default)',
              }}
            >
              <button
                type="button"
                onClick={() => onSelect(s.id)}
                className="flex items-center gap-2 px-3 py-1.5"
                style={{
                  fontSize: 'var(--brio-font-size-base)',
                  fontWeight: isActive ? 600 : 400,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                v{s.version}
                <StatusBadge status={s.status} />
                {isActiveConfirmed && (
                  <span title="Active confirmed version" style={{ color: 'var(--brio-green)' }}><Lock size={12} /></span>
                )}
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const label = `v${s.version} (${s.status})`;
                    // eslint-disable-next-line no-restricted-globals, no-alert
                    if (window.confirm(`Delete scope ${label}? This cannot be undone.`)) {
                      onDelete(s.id);
                    }
                  }}
                  title="Delete this version"
                  aria-label={`Delete v${s.version}`}
                  style={{
                    padding: '0 8px',
                    background: 'transparent',
                    border: 'none',
                    borderLeft: '1px solid var(--brio-border)',
                    color: 'var(--brio-red)',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ============================================================================
// CONFIRMATION DIALOG
// ============================================================================

function ConfirmationPanel({ validation, onConfirm, onCancel }) {
  return (
    <BrioCard className="mt-4">
      <div className="p-4">
        <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--brio-text-primary)' }}>
          <AlertCircle size={16} />
          Confirm Scope of Work
        </h3>
        {!validation.valid ? (
          <>
            <p className="mb-3" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}>
              The following issues must be resolved before confirmation:
            </p>
            <ul className="mb-4 space-y-1">
              {validation.errors.map((err, i) => (
                <li key={i} className="flex items-start gap-2" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-red)' }}>
                  <X size={14} className="flex-shrink-0 mt-0.5" />
                  {err}
                </li>
              ))}
            </ul>
            <BrioButton variant="ghost" onClick={onCancel}>Close</BrioButton>
          </>
        ) : (
          <>
            <p className="mb-3" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}>
              This will lock the scope as the authoritative basis for this project.
              Future changes will require creating a new revision.
            </p>
            <BrioAlert>
              This action cannot be undone. The confirmed scope will become read-only.
            </BrioAlert>
            <div className="flex gap-2 mt-4">
              <BrioButton variant="primary" onClick={onConfirm}>
                <Lock size={14} className="mr-1" /> Confirm &amp; Lock
              </BrioButton>
              <BrioButton variant="ghost" onClick={onCancel}>Cancel</BrioButton>
            </div>
          </>
        )}
      </div>
    </BrioCard>
  );
}


// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function ContractsScopePage({
  activeProjects,
  filteredProjects,
  activeUsers,
  currentUser,
  isAdmin,
  contractScopesHook,
  scopeTemplatesHook,
  supabaseInquiries,
  settingsHook,
}) {
  const {
    scopes: allScopes,
    getScopesForProject,
    getScopesForInquiry,
    createScope,
    addImportedScope,
    updateScope,
    updateStage,
    reorderStages,
    toggleDeliverable,
    addCustomDeliverable,
    removeCustomDeliverable,
    updateDeliverables,
    moveToNegotiation,
    confirmScope,
    createRevision,
    deleteScope,
  } = contractScopesHook;

  // contract_scopes and scope_templates are left out of the initial settings
  // fetch because of their size — pull them now that a page needs them.
  const ensureScopes = contractScopesHook?.ensureLoaded;
  const ensureTemplates = scopeTemplatesHook?.ensureLoaded;
  useEffect(() => { ensureScopes?.(); ensureTemplates?.(); }, [ensureScopes, ensureTemplates]);

  // Confirmed templates from admin settings ("Base Templates")
  const confirmedTemplates = useMemo(() => {
    if (!scopeTemplatesHook) return [];
    return scopeTemplatesHook.templates.filter(t => t.status === 'confirmed');
  }, [scopeTemplatesHook]);

  // Active inquiries for the inquiry selector
  const activeInquiries = useMemo(() => {
    if (!supabaseInquiries?.inquiries) return [];
    return supabaseInquiries.inquiries.filter(i => i.status === 'active' || i.status === 'followup' || i.status === 'proposal');
  }, [supabaseInquiries]);

  // Resolve user ID/name for "confirmed by" display
  const resolveUserName = useCallback((userId) => {
    if (!userId) return '';
    if (!activeUsers || activeUsers.length === 0) return String(userId);
    // Try numeric ID match
    const byId = activeUsers.find(u => String(u.id) === String(userId));
    if (byId) return byId.name;
    // Try auth UUID match
    const byAuth = activeUsers.find(u => u.authUserId === userId || u.auth_user_id === userId);
    if (byAuth) return byAuth.name;
    // Try name match (already a name)
    const byName = activeUsers.find(u => u.name === userId);
    if (byName) return byName.name;
    return String(userId);
  }, [activeUsers]);

  // Local UI state
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedInquiryId, setSelectedInquiryId] = useState(null);
  const [activeScopeId, setActiveScopeId] = useState(null);
  const [expandedStages, setExpandedStages] = useState({});
  const [showConfirmPanel, setShowConfirmPanel] = useState(false);
  const [notesInput, setNotesInput] = useState('');
  const [negotiationNotesInput, setNegotiationNotesInput] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [interiorDeliverablesExpanded, setInteriorDeliverablesExpanded] = useState(false);
  const [editingTemplateIntroduction, setEditingTemplateIntroduction] = useState(false);
  const [templateIntroductionDraft, setTemplateIntroductionDraft] = useState('');
  const [allScopesSortBy, setAllScopesSortBy] = useState('projectName');
  const [allScopesSortDir, setAllScopesSortDir] = useState('asc');

  // Edit mode for confirmed/archived scopes
  const [editingConfirmed, setEditingConfirmed] = useState(false);

  // PDF import wizard
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importNotice, setImportNotice] = useState(null);

  // Derived — scopes for whichever entity is currently selected (project or inquiry)
  const projectScopes = useMemo(() => {
    if (selectedInquiryId) return getScopesForInquiry(selectedInquiryId);
    if (selectedProjectId) return getScopesForProject(selectedProjectId);
    return [];
  }, [selectedProjectId, selectedInquiryId, getScopesForProject, getScopesForInquiry]);

  const activeScope = useMemo(
    () => projectScopes.find(s => s.id === activeScopeId) || null,
    [projectScopes, activeScopeId]
  );

  const isConfirmedOrArchived = activeScope?.status === SCOPE_STATUSES.CONFIRMED || activeScope?.status === SCOPE_STATUSES.ARCHIVED;
  const readOnly = isConfirmedOrArchived && !editingConfirmed;

  const validation = useMemo(
    () => activeScope ? validateScopeForConfirmation(activeScope) : { valid: false, errors: [] },
    [activeScope]
  );

  // Auto-select scope when projectScopes loads after project selection
  // (handles race condition where user selects project before scopes are loaded)
  useEffect(() => {
    if ((!selectedProjectId && !selectedInquiryId) || projectScopes.length === 0) return;
    // If activeScopeId is null or not found in current projectScopes, auto-select
    const activeStillValid = activeScopeId && projectScopes.some(s => s.id === activeScopeId);
    if (!activeStillValid) {
      // Prefer the confirmed active scope, otherwise the latest
      const confirmed = projectScopes.find(s => s.status === 'confirmed' && s.isActiveVersion);
      const latest = confirmed || projectScopes[projectScopes.length - 1];
      setActiveScopeId(latest.id);
      setNotesInput(latest.notes || '');
      setNegotiationNotesInput(latest.negotiationNotes || '');
    }
  }, [selectedProjectId, selectedInquiryId, projectScopes, activeScopeId]);

  // When project changes, clear inquiry mode and auto-select latest scope
  const handleProjectChange = useCallback((projectId) => {
    setSelectedInquiryId(null);
    setSelectedProjectId(projectId);
    setActiveScopeId(null);
    setExpandedStages({});
    setShowConfirmPanel(false);
    setEditingConfirmed(false);
    if (projectId) {
      const scopes = getScopesForProject(projectId);
      if (scopes.length > 0) {
        const latest = scopes[scopes.length - 1];
        setActiveScopeId(latest.id);
        setNotesInput(latest.notes || '');
        setNegotiationNotesInput(latest.negotiationNotes || '');
      }
    }
  }, [getScopesForProject]);

  const handleSelectScope = useCallback((scopeId) => {
    setActiveScopeId(scopeId);
    setExpandedStages({});
    setShowConfirmPanel(false);
    setEditingConfirmed(false);
    const scope = projectScopes.find(s => s.id === scopeId);
    if (scope) {
      setNotesInput(scope.notes || '');
      setNegotiationNotesInput(scope.negotiationNotes || '');
    }
  }, [projectScopes]);

  const handleImportFromPdf = useCallback((importedScope) => {
    addImportedScope(importedScope);
    setShowImportWizard(false);
    if (importedScope.projectId) {
      // Switch the page to the project the import was attached to so the
      // user lands on the new draft scope ready for further editing.
      setSelectedProjectId(String(importedScope.projectId));
      setActiveScopeId(importedScope.id);
      setNotesInput('');
      setNegotiationNotesInput('');
      setExpandedStages({});
      setImportNotice(null);
    } else {
      setImportNotice('Saved as an unattached draft. Pick a project from the dropdown above to view and link it.');
    }
  }, [addImportedScope]);

  // One-time migration: backfill inquiryId on scopes that were stored with a raw
  // inquiry numeric ID as their projectId (the old broken path). Runs once when
  // both scopes and inquiries are loaded; silently skips if nothing to migrate.
  const orphanMigrationRun = useRef(false);
  useEffect(() => {
    if (orphanMigrationRun.current) return;
    if (!allScopes.length || !supabaseInquiries?.inquiries?.length) return;
    const projectIdSet = new Set(activeProjects.map(p => String(p.id)));
    const inquiryIdSet = new Set(supabaseInquiries.inquiries.map(i => String(i.id)));
    const orphans = allScopes.filter(
      s => s.inquiryId == null &&
           s.projectId != null &&
           !projectIdSet.has(String(s.projectId)) &&
           inquiryIdSet.has(String(s.projectId))
    );
    if (orphans.length === 0) {
      orphanMigrationRun.current = true;
      return;
    }
    orphans.forEach(s => {
      contractScopesHook.updateScope(s.id, { inquiryId: s.projectId, projectId: null });
    });
    orphanMigrationRun.current = true;
  }, [allScopes, supabaseInquiries?.inquiries, activeProjects, contractScopesHook]);

  const handleCreateNew = useCallback(() => {
    if (!selectedProjectId && !selectedInquiryId) return;
    const template = selectedTemplateId
      ? confirmedTemplates.find(t => t.id === selectedTemplateId)
      : confirmedTemplates[0];

    let scope;
    if (selectedInquiryId) {
      const inq = (supabaseInquiries?.inquiries || []).find(i => String(i.id) === String(selectedInquiryId));
      const client = inq
        ? (inq.clientNameOverride || inq.clientOrgNameOverride || inq.clientName || inq.client_name || `Inquiry #${inq.id}`)
        : `Inquiry #${selectedInquiryId}`;
      const title = `${client} — Scope of Work`;
      scope = createScope({ inquiryId: selectedInquiryId, projectId: null }, title, template || undefined);
    } else {
      const project = activeProjects.find(p => String(p.id) === String(selectedProjectId));
      const title = project ? `${project.name} — Scope of Work` : 'Scope of Work';
      scope = createScope(selectedProjectId, title, template || undefined);
    }

    setActiveScopeId(scope.id);
    setNotesInput('');
    setNegotiationNotesInput('');
    setExpandedStages({});
  }, [selectedProjectId, selectedInquiryId, activeProjects, supabaseInquiries, createScope, selectedTemplateId, confirmedTemplates]);

  const handleToggleExpand = useCallback((stageKey) => {
    setExpandedStages(prev => ({ ...prev, [stageKey]: !prev[stageKey] }));
  }, []);

  const handleToggleStageSelected = useCallback((stageKey) => {
    if (!activeScope || readOnly) return;
    const stage = activeScope.stages.find(s => s.key === stageKey);
    updateStage(activeScope.id, stageKey, { selected: !stage.selected });
  }, [activeScope, readOnly, updateStage]);

  const handleToggleDeliverable = useCallback((stageKey, deliverableType, deliverableId) => {
    if (!activeScope || readOnly) return;
    toggleDeliverable(activeScope.id, stageKey, deliverableType, deliverableId);
  }, [activeScope, readOnly, toggleDeliverable]);

  const handleAddCustom = useCallback((stageKey, label) => {
    if (!activeScope || readOnly) return;
    addCustomDeliverable(activeScope.id, stageKey, label);
  }, [activeScope, readOnly, addCustomDeliverable]);

  const handleRemoveCustom = useCallback((stageKey, deliverableId) => {
    if (!activeScope || readOnly) return;
    removeCustomDeliverable(activeScope.id, stageKey, deliverableId);
  }, [activeScope, readOnly, removeCustomDeliverable]);

  const handleRenameCustom = useCallback((stageKey, deliverableId, newLabel) => {
    if (!activeScope || readOnly) return;
    const stage = activeScope.stages.find(s => s.key === stageKey);
    if (!stage) return;
    const updated = stage.deliverables.custom.map(d =>
      d.id === deliverableId ? { ...d, label: newLabel } : d
    );
    updateDeliverables(activeScope.id, stageKey, { custom: updated });
  }, [activeScope, readOnly, updateDeliverables]);

  // Delete a scope version. Called from the version-list X button —
  // already guarded against deleting the live confirmed scope and
  // already gated to admins in the rendering component, but we
  // re-check here so a misuse of the hook can't bypass the rules.
  const handleDeleteVersion = useCallback((scopeId) => {
    const scope = projectScopes.find(s => s.id === scopeId);
    if (!scope || !isAdmin) return;
    if (scope.isActiveVersion && scope.status === SCOPE_STATUSES.CONFIRMED) return;
    deleteScope(scopeId);
    if (activeScopeId === scopeId) {
      // Pick any remaining scope on this project, otherwise clear.
      const remaining = projectScopes.filter(s => s.id !== scopeId);
      const next = remaining.find(s => s.status === SCOPE_STATUSES.CONFIRMED && s.isActiveVersion)
        || remaining[remaining.length - 1]
        || null;
      setActiveScopeId(next ? next.id : null);
      setNotesInput(next?.notes || '');
      setNegotiationNotesInput(next?.negotiationNotes || '');
      setExpandedStages({});
    }
  }, [projectScopes, isAdmin, deleteScope, activeScopeId]);

  const handleUpdateDescription = useCallback((stageKey, description) => {
    if (!activeScope || readOnly) return;
    updateStage(activeScope.id, stageKey, { description });
  }, [activeScope, readOnly, updateStage]);

  const handleMarkDescriptionReviewed = useCallback((stageKey) => {
    if (!activeScope || readOnly) return;
    const stage = activeScope.stages.find(s => s.key === stageKey);
    updateStage(activeScope.id, stageKey, { descriptionReviewed: !stage.descriptionReviewed });
  }, [activeScope, readOnly, updateStage]);

  // Update deliverables (for quantity changes within StageCard)
  const handleUpdateStageDeliverables = useCallback((stageKey, stageUpdates) => {
    if (!activeScope || readOnly) return;
    updateStage(activeScope.id, stageKey, stageUpdates);
  }, [activeScope, readOnly, updateStage]);

  // Title editing
  const handleSaveTitle = useCallback(() => {
    if (!activeScope || !titleDraft.trim()) return;
    updateScope(activeScope.id, { title: titleDraft.trim() });
    setEditingTitle(false);
  }, [activeScope, titleDraft, updateScope]);

  const handleSaveNotes = useCallback(() => {
    if (!activeScope || readOnly) return;
    updateScope(activeScope.id, { notes: notesInput, negotiationNotes: negotiationNotesInput });
  }, [activeScope, readOnly, notesInput, negotiationNotesInput, updateScope]);

  const handleMoveToNegotiation = useCallback(() => {
    if (!activeScope) return;
    moveToNegotiation(activeScope.id);
  }, [activeScope, moveToNegotiation]);

  const handleConfirm = useCallback(() => {
    if (!activeScope || !validation.valid) return;
    confirmScope(activeScope.id, currentUser?.id || currentUser?.name);
    setShowConfirmPanel(false);
  }, [activeScope, validation, confirmScope, currentUser]);

  const handleCreateRevision = useCallback(() => {
    if (!activeScope) return;
    const rev = createRevision(activeScope.id);
    if (rev) {
      setActiveScopeId(rev.id);
      setNotesInput('');
      setNegotiationNotesInput('');
      setExpandedStages({});
    }
  }, [activeScope, createRevision]);

  const handleDeleteDraft = useCallback(() => {
    if (!activeScope || activeScope.status !== SCOPE_STATUSES.DRAFT) return;
    if (!window.confirm('Delete this draft scope? This cannot be undone.')) return;
    deleteScope(activeScope.id);
    setActiveScopeId(null);
  }, [activeScope, deleteScope]);

  const handleDeleteConfirmed = useCallback(() => {
    if (!activeScope) return;
    const label = activeScope.status === SCOPE_STATUSES.CONFIRMED ? 'confirmed' : 'archived';
    if (!window.confirm(`Delete this ${label} scope (v${activeScope.version})? This cannot be undone.`)) return;
    deleteScope(activeScope.id);
    setActiveScopeId(null);
  }, [activeScope, deleteScope]);

  // Export scope as PDF
  const [exporting, setExporting] = useState(false);
  const handleExportPdf = useCallback(async () => {
    if (!activeScope || exporting) return;
    setExporting(true);
    try {
      const proj = activeProjects.find(p => String(p.id) === String(selectedProjectId));
      const inq = selectedInquiryId
        ? (supabaseInquiries?.inquiries || []).find(i => String(i.id) === String(selectedInquiryId))
        : null;
      const displayProjectName = proj?.name
        || (inq ? [inq.projectName, inq.projectType].filter(Boolean).join(' — ') : '');
      const displayClientName = proj?.clientName
        || (inq ? (inq.clientNameOverride || inq.clientOrgNameOverride || inq.clientName || '') : '');
      const displaySlug = (displayProjectName || displayClientName || 'inquiry')
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-]/g, '')
        .substring(0, 30);

      const branding = settingsHook?.invoiceBranding;
      const headerUrl = resolveBrandingImageSrc(branding?.offices?.mumbai?.headerImage);
      const html = buildScopeHtml({
        title: activeScope.title || 'Scope of Work',
        version: activeScope.version,
        projectName: displayProjectName,
        clientName: displayClientName,
        templateIntroduction: activeScope.templateIntroduction,
        interiorWarmShellSelected: activeScope.interiorWarmShellSelected,
        interiorFfeSelected: activeScope.interiorFfeSelected,
        interiorIntroduction: activeScope.interiorIntroduction,
        interiorDeliverables: activeScope.interiorDeliverables,
        stages: activeScope.stages,
        pdfStyles: settingsHook?.scopePdfStyles,
        headerImageUrl: headerUrl,
        officeName: branding?.offices?.mumbai?.label || 'IN',
        paymentStages: [],
      });

      const filename = `BRIO_SOW_${displaySlug}_v${activeScope.version}.pdf`;

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        alert('Session expired. Please log in again.');
        setExporting(false);
        return;
      }

      const response = await fetch('/api/scope-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ html, filename }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('[scope-pdf] Export failed:', err);
      alert('Failed to export PDF: ' + err.message);
    } finally {
      setExporting(false);
    }
  }, [activeScope, exporting, activeProjects, selectedProjectId, selectedInquiryId, supabaseInquiries, settingsHook]);

  // Select all / deselect all stages helper
  const handleSelectAllStages = useCallback((selectAll) => {
    if (!activeScope || readOnly) return;
    activeScope.stages.forEach(stage => {
      updateStage(activeScope.id, stage.key, { selected: selectAll });
    });
  }, [activeScope, readOnly, updateStage]);

  // Drag-and-drop stage reordering
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const stageIds = useMemo(
    () => activeScope ? activeScope.stages.map(s => s.key) : [],
    [activeScope]
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !activeScope) return;
    const oldIndex = stageIds.indexOf(active.id);
    const newIndex = stageIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = [...stageIds];
    newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, active.id);
    reorderStages(activeScope.id, newOrder);
  }, [activeScope, stageIds, reorderStages]);

  const selectedProject = activeProjects.find(p => String(p.id) === String(selectedProjectId));
  const selectedInquiry = useMemo(
    () => selectedInquiryId
      ? (supabaseInquiries?.inquiries || []).find(i => String(i.id) === String(selectedInquiryId)) || null
      : null,
    [selectedInquiryId, supabaseInquiries?.inquiries]
  );
  const selectedStageCount = activeScope ? activeScope.stages.filter(s => s.selected).length : 0;

  return (
    <>
      <BrioPageHeader
        title="Scope"
        subtitle="Define and manage scope of work for project contracts"
        right={
          <div className="flex items-center gap-2">
            <BrioButton
              variant="secondary"
              onClick={() => setShowImportWizard(true)}
              style={{ whiteSpace: 'nowrap' }}
              title="Import an existing scope-of-work PDF and structure it against a base template"
            >
              <Upload size={14} /> Import PDF
            </BrioButton>
            {(selectedProjectId || selectedInquiryId) && (
              <>
                {confirmedTemplates.length > 0 && (
                  <BrioSelect
                    value={selectedTemplateId}
                    onChange={e => setSelectedTemplateId(e.target.value)}
                    style={{ fontSize: 'var(--brio-font-size-s)', maxWidth: 220 }}
                  >
                    <option value="">Base Template…</option>
                    {confirmedTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </BrioSelect>
                )}
                <BrioButton variant="primary" onClick={handleCreateNew} style={{ whiteSpace: 'nowrap' }}>
                  <Plus size={14} /> New Scope
                </BrioButton>
              </>
            )}
          </div>
        }
      />

      <BrioFrame>
        {importNotice && (
          <BrioAlert label="Imported scope" className="mb-3">
            <div className="flex items-start gap-3">
              <span style={{ flex: 1 }}>{importNotice}</span>
              <button
                type="button"
                onClick={() => setImportNotice(null)}
                aria-label="Dismiss"
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--brio-text-muted)',
                }}
              >
                <X size={14} />
              </button>
            </div>
          </BrioAlert>
        )}

        {/* Project selector */}
        <BrioCard className="mb-4">
          <div className="p-4">
            <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
              Project
            </label>
            <BrioSelect
              value={selectedProjectId}
              onChange={e => handleProjectChange(e.target.value)}
              style={{ maxWidth: 400 }}
            >
              <option value="">Select a project…</option>
              {[...(filteredProjects || activeProjects)].sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || '')).map(p => (
                <option key={p.id} value={p.id}>{p.projectCode ? `${p.projectCode} \u2014 ${p.name}` : p.name}</option>
              ))}
            </BrioSelect>
            {selectedProject && (
              <p className="mt-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                {selectedProject.client_name || selectedProject.clientName || ''}
                {selectedProject.status ? ` · ${selectedProject.status}` : ''}
              </p>
            )}
            {selectedInquiry && !selectedProject && (
              <p className="mt-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', background: '#FFF3CD', color: '#856404', marginRight: 6, fontWeight: 600 }}>Inquiry</span>
                {[
                  selectedInquiry.clientNameOverride || selectedInquiry.clientOrgNameOverride || selectedInquiry.clientName || '',
                  selectedInquiry.projectType || '',
                  selectedInquiry.locationCity || selectedInquiry.siteCity || '',
                ].filter(Boolean).join(' · ')}
                <span style={{ color: 'var(--brio-text-muted)', fontStyle: 'italic', marginLeft: 6 }}>
                  · Scope will auto-link when this inquiry is converted to a project
                </span>
              </p>
            )}

            {/* Active inquiries selector */}
            {activeInquiries.length > 0 && (
              <div className="mt-3">
                <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                  Active Inquiries
                </label>
                <BrioSelect
                  value={selectedInquiryId ? `inq:${selectedInquiryId}` : ''}
                  onChange={e => {
                    const raw = e.target.value;
                    if (!raw) return;
                    if (raw.startsWith('proj:')) {
                      handleProjectChange(raw.slice(5));
                    } else if (raw.startsWith('inq:')) {
                      const iid = raw.slice(4);
                      setSelectedInquiryId(iid);
                      setSelectedProjectId('');
                      setActiveScopeId(null);
                      setExpandedStages({});
                      setShowConfirmPanel(false);
                      setEditingConfirmed(false);
                      const inqScopes = getScopesForInquiry(iid);
                      if (inqScopes.length > 0) {
                        const latest = inqScopes[inqScopes.length - 1];
                        setActiveScopeId(latest.id);
                        setNotesInput(latest.notes || '');
                        setNegotiationNotesInput(latest.negotiationNotes || '');
                      }
                    }
                  }}
                  style={{ maxWidth: 400 }}
                >
                  <option value="">Select an inquiry…</option>
                  {activeInquiries.map(inq => {
                    const hasProject = !!(inq.convertedProjectId);
                    const optValue = hasProject ? `proj:${inq.convertedProjectId}` : `inq:${inq.id}`;
                    const client = inq.clientNameOverride || inq.clientOrgNameOverride || inq.clientName || inq.client_name || '';
                    const location = inq.locationCity || inq.siteCity || inq.location_city || '';
                    const label = [client, location].filter(Boolean).join(', ') || inq.projectName || inq.project_name || inq.name || inq.title || `Inquiry #${inq.id}`;
                    return (
                      <option key={inq.id} value={optValue}>
                        {label}{hasProject ? ' (has project)' : ''}
                      </option>
                    );
                  })}
                </BrioSelect>
                <p className="mt-0.5" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
                  Create or view a scope of work for an active inquiry without converting it first.
                </p>
              </div>
            )}
          </div>
        </BrioCard>

        {/* No project or inquiry selected */}
        {!selectedProjectId && !selectedInquiryId && (
          <p style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-muted)', padding: 'var(--brio-spacing-md) 0' }}>
            Select a project or inquiry above to view or create its scope of work.
          </p>
        )}

        {/* Project/inquiry selected but no scopes */}
        {(selectedProjectId || selectedInquiryId) && projectScopes.length === 0 && (
          <BrioCard className="mb-4">
            <div className="p-4 flex items-center justify-between gap-4">
              <span style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}>
                No scope of work yet.
              </span>
              {confirmedTemplates.length > 0 ? (
                <div className="flex items-center gap-2">
                  <BrioSelect
                    value={selectedTemplateId}
                    onChange={e => setSelectedTemplateId(e.target.value)}
                    style={{ fontSize: 'var(--brio-font-size-s)', maxWidth: 200 }}
                  >
                    <option value="">Template…</option>
                    {confirmedTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </BrioSelect>
                  <BrioButton variant="primary" onClick={handleCreateNew} style={{ whiteSpace: 'nowrap' }}>
                    <Plus size={14} /> Create Scope
                  </BrioButton>
                </div>
              ) : (
                isAdmin ? (
                  <BrioButton variant="primary" onClick={handleCreateNew} style={{ whiteSpace: 'nowrap' }}>
                    <Plus size={14} /> Create Scope
                  </BrioButton>
                ) : (
                  <span style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
                    No templates configured. Ask an admin to add one in Settings.
                  </span>
                )
              )}
            </div>
          </BrioCard>
        )}

        {/* Version list */}
        {projectScopes.length > 0 && (
          <ScopeVersionList
            scopes={projectScopes}
            activeScopeId={activeScopeId}
            onSelect={handleSelectScope}
            onCreateNew={handleCreateNew}
            onDelete={handleDeleteVersion}
            isAdmin={isAdmin}
          />
        )}

        {/* Active scope editor */}
        {activeScope && (
          <>
            {/* Scope header bar */}
            <BrioCard className="mb-4">
              <div className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    {editingTitle ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="brio-modal-input font-semibold"
                          style={{ width: 'auto', minWidth: 200 }}
                          value={titleDraft}
                          onChange={e => setTitleDraft(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                          autoFocus
                        />
                        <button type="button" onClick={handleSaveTitle} className="brio-icon-btn brio-icon-btn--small" style={{ color: 'var(--brio-gold)' }}><Check size={16} /></button>
                        <button type="button" onClick={() => setEditingTitle(false)} className="brio-icon-btn brio-icon-btn--small"><X size={16} /></button>
                      </div>
                    ) : (
                      <h2
                        className="text-base font-semibold cursor-pointer"
                        style={{ color: 'var(--brio-text-primary)' }}
                        onClick={() => { setTitleDraft(activeScope.title); setEditingTitle(true); }}
                        title="Click to rename"
                      >
                        {activeScope.title}
                        <Pencil size={11} className="inline ml-1.5 opacity-40" />
                      </h2>
                    )}
                    <StatusBadge status={activeScope.status} />
                    {activeScope.inquiryId && !activeScope.projectId && (
                      <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', background: '#FFF3CD', color: '#856404', fontWeight: 600 }} title="Linked to an inquiry — will auto-link when the inquiry is converted to a project">
                        Inquiry Draft
                      </span>
                    )}
                    <span style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>v{activeScope.version}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Status actions */}
                    {activeScope.status === SCOPE_STATUSES.DRAFT && (
                      <>
                        <button type="button" onClick={handleExportPdf} disabled={exporting} title="Export PDF" className="brio-icon-btn">
                          <Download size={15} />
                        </button>
                        <BrioButton variant="ghost" onClick={handleDeleteDraft}>
                          <Trash2 size={14} className="mr-1" /> Delete Draft
                        </BrioButton>
                        <BrioButton variant="primary" onClick={handleMoveToNegotiation}>
                          <Send size={14} className="mr-1" /> Move to Negotiation
                        </BrioButton>
                      </>
                    )}
                    {activeScope.status === SCOPE_STATUSES.NEGOTIATION && (
                      <>
                      <button type="button" onClick={handleExportPdf} disabled={exporting} title="Export PDF" className="brio-icon-btn">
                        <Download size={15} />
                      </button>
                      <BrioButton
                        variant="primary"
                        onClick={() => setShowConfirmPanel(true)}
                      >
                        <Lock size={14} className="mr-1" /> Confirm Scope
                      </BrioButton>
                      </>
                    )}
                    {activeScope.status === SCOPE_STATUSES.CONFIRMED && (
                      <div className="brio-action-group">
                        <button type="button" onClick={handleExportPdf} disabled={exporting} title="Export PDF" className="brio-icon-btn">
                          <Download size={15} />
                        </button>
                        <button type="button" onClick={handleDeleteConfirmed} title="Delete" className="brio-icon-btn brio-icon-btn--delete">
                          <Trash2 size={15} />
                        </button>
                        {editingConfirmed ? (
                          <button
                            type="button"
                            onClick={() => setEditingConfirmed(false)}
                            title="Done Editing"
                            className="brio-icon-btn"
                            style={{ backgroundColor: 'var(--brio-gold)', color: 'var(--brio-white)' }}
                          >
                            <Lock size={15} />
                          </button>
                        ) : (
                          <button type="button" onClick={() => setEditingConfirmed(true)} title="Edit" className="brio-icon-btn brio-icon-btn--edit">
                            <Pencil size={15} />
                          </button>
                        )}
                        <button type="button" onClick={handleCreateRevision} title="New Revision" className="brio-icon-btn">
                          <Copy size={15} />
                        </button>
                      </div>
                    )}
                    {activeScope.status === SCOPE_STATUSES.ARCHIVED && (
                      <div className="brio-action-group">
                        <button type="button" onClick={handleExportPdf} disabled={exporting} title="Export PDF" className="brio-icon-btn">
                          <Download size={15} />
                        </button>
                        <button type="button" onClick={handleDeleteConfirmed} title="Delete" className="brio-icon-btn brio-icon-btn--delete">
                          <Trash2 size={15} />
                        </button>
                        {editingConfirmed ? (
                          <button
                            type="button"
                            onClick={() => setEditingConfirmed(false)}
                            title="Done Editing"
                            className="brio-icon-btn"
                            style={{ backgroundColor: 'var(--brio-gold)', color: 'var(--brio-white)' }}
                          >
                            <Lock size={15} />
                          </button>
                        ) : (
                          <button type="button" onClick={() => setEditingConfirmed(true)} title="Edit" className="brio-icon-btn brio-icon-btn--edit">
                            <Pencil size={15} />
                          </button>
                        )}
                      </div>
                    )}
                    {readOnly && (
                      <span className="flex items-center gap-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                        <Eye size={12} /> Read-only
                      </span>
                    )}
                    {editingConfirmed && isConfirmedOrArchived && (
                      <span className="flex items-center gap-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-gold)' }}>
                        <Pencil size={12} /> Editing
                      </span>
                    )}
                  </div>
                </div>

                {/* Confirmed info */}
                {activeScope.confirmedAt && (
                  <p className="mt-2" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                    Confirmed on {new Date(activeScope.confirmedAt).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                    {activeScope.confirmedBy && ` by ${resolveUserName(activeScope.confirmedBy)}`}
                  </p>
                )}

                {/* Timestamps */}
                <p className="mt-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
                  Created {new Date(activeScope.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                  {' · '}
                  Updated {new Date(activeScope.updatedAt).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </p>
              </div>
            </BrioCard>

            {/* Confirmation panel */}
            {showConfirmPanel && (
              <ConfirmationPanel
                validation={validation}
                onConfirm={handleConfirm}
                onCancel={() => setShowConfirmPanel(false)}
              />
            )}

            {/* Stage selection controls */}
            {!readOnly && (
              <div className="flex items-center justify-between mb-3">
                <span style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}>
                  {selectedStageCount} of {activeScope.stages.length} stages selected
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleSelectAllStages(true)}
                    className="underline"
                    style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-gold)' }}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectAllStages(false)}
                    className="underline"
                    style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}
                  >
                    Deselect all
                  </button>
                </div>
              </div>
            )}

            {/* Template Introduction */}
            {(activeScope.templateIntroduction || !readOnly) && (
              <BrioCard className="mb-4">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold uppercase tracking-wide" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
                      Introduction
                    </span>
                    {!readOnly && !editingTemplateIntroduction && (
                      <button
                        type="button"
                        onClick={() => { setTemplateIntroductionDraft(activeScope.templateIntroduction || ''); setEditingTemplateIntroduction(true); }}
                        className="underline"
                        style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-gold)' }}
                      >
                        {activeScope.templateIntroduction ? 'Edit' : 'Add'}
                      </button>
                    )}
                  </div>
                  {editingTemplateIntroduction ? (
                    <div>
                      <textarea
                        value={templateIntroductionDraft}
                        onChange={e => setTemplateIntroductionDraft(e.target.value)}
                        rows={Math.max(4, Math.min(20, (templateIntroductionDraft.match(/\n/g) || []).length + 3))}
                        className="brio-input"
                        style={{ width: '100%', fontSize: 'var(--brio-font-size-base)', padding: 8, lineHeight: 1.5 }}
                        placeholder="Introduction shown at the top of the scope of work, before the first stage. Use **text** for bold sub-headers."
                      />
                      <div className="flex gap-2 mt-1">
                        <BrioButton variant="primary" onClick={() => { updateScope(activeScope.id, { templateIntroduction: templateIntroductionDraft }); setEditingTemplateIntroduction(false); }}>Save</BrioButton>
                        <BrioButton variant="ghost" onClick={() => { setTemplateIntroductionDraft(activeScope.templateIntroduction || ''); setEditingTemplateIntroduction(false); }}>Cancel</BrioButton>
                      </div>
                    </div>
                  ) : activeScope.templateIntroduction ? (
                    <ScopeSowMarkup
                      text={activeScope.templateIntroduction}
                      className="leading-relaxed"
                      style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-primary)', fontWeight: 300 }}
                    />
                  ) : (
                    <p style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>
                      No introduction. Click &quot;Add&quot; to write one.
                    </p>
                  )}
                </div>
              </BrioCard>
            )}

            {/* Summary of Interior Deliverables — selectable + collapsible */}
            {(activeScope.interiorDeliverables?.length > 0 || !readOnly) && (
              <InteriorDeliverablesPanel
                scope={activeScope}
                readOnly={readOnly}
                expanded={interiorDeliverablesExpanded}
                onToggleExpand={() => setInteriorDeliverablesExpanded(prev => !prev)}
                onUpdateScope={(updates) => updateScope(activeScope.id, updates)}
              />
            )}

            {/* Stage cards with drag-and-drop reordering */}
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={stageIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-2 mb-6">
                  {activeScope.stages.map(stage => (
                    <SortableStageCard
                      key={stage.key}
                      id={stage.key}
                      stage={stage}
                      readOnly={readOnly}
                      expanded={!!expandedStages[stage.key]}
                      onToggleExpand={() => handleToggleExpand(stage.key)}
                      onToggleSelected={() => handleToggleStageSelected(stage.key)}
                      onToggleDeliverable={(type, id) => handleToggleDeliverable(stage.key, type, id)}
                      onAddCustom={(label) => handleAddCustom(stage.key, label)}
                      onRemoveCustom={(id) => handleRemoveCustom(stage.key, id)}
                      onRenameCustom={(id, label) => handleRenameCustom(stage.key, id, label)}
                      onMarkDescriptionReviewed={() => handleMarkDescriptionReviewed(stage.key)}
                      onUpdateDescription={(desc) => handleUpdateDescription(stage.key, desc)}
                      onUpdateStage={(updates) => handleUpdateStageDeliverables(stage.key, updates)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Notes section */}
            <BrioCard className="mb-6">
              <div className="p-4">
                <BrioSection title="Notes">
                  <div className="space-y-3">
                    <div>
                      <label className="font-semibold mb-1 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>General Notes</label>
                      <textarea
                        className="brio-modal-input"
                        style={{ minHeight: 60 }}
                        value={notesInput}
                        onChange={e => setNotesInput(e.target.value)}
                        disabled={readOnly}
                        placeholder="Internal notes about this scope…"
                      />
                    </div>
                    <div>
                      <label className="font-semibold mb-1 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>Negotiation Notes</label>
                      <textarea
                        className="brio-modal-input"
                        style={{ minHeight: 60 }}
                        value={negotiationNotesInput}
                        onChange={e => setNegotiationNotesInput(e.target.value)}
                        disabled={readOnly}
                        placeholder="Notes from client discussions, revisions, agreements…"
                      />
                    </div>
                    {!readOnly && (
                      <BrioButton variant="ghost" onClick={handleSaveNotes}>
                        Save Notes
                      </BrioButton>
                    )}
                  </div>
                </BrioSection>
              </div>
            </BrioCard>

            {/* Full Document Preview */}
            <BrioCard className="mb-6">
              <div className="p-4">
                <button
                  type="button"
                  className="flex items-center gap-2 font-semibold uppercase tracking-wide w-full"
                  style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  onClick={() => setShowDocumentPreview(prev => !prev)}
                >
                  {showDocumentPreview ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Eye size={14} />
                  Full Document Preview
                </button>
                {showDocumentPreview && (
                  <div className="mt-4 space-y-6" style={{ maxWidth: 700 }}>
                    <h2 className="text-lg font-semibold" style={{ color: 'var(--brio-text-primary)' }}>{activeScope.title}</h2>

                    {/* Template Introduction in preview */}
                    {activeScope.templateIntroduction && (
                      <ScopeSowMarkup
                        text={activeScope.templateIntroduction}
                        className="leading-relaxed mb-3"
                        style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-primary)', fontWeight: 300 }}
                      />
                    )}

                    {/* Summary of Interior Deliverables in preview — sectioned */}
                    {(() => {
                      const selectedSectionMap = {
                        [INTERIOR_SECTIONS.WARM_SHELL]: !!activeScope.interiorWarmShellSelected,
                        [INTERIOR_SECTIONS.FFE]: !!activeScope.interiorFfeSelected,
                      };
                      const allItems = activeScope.interiorDeliverables || [];
                      const sectionsToRender = INTERIOR_SECTION_ORDER
                        .filter(k => selectedSectionMap[k])
                        .map(k => ({
                          key: k,
                          label: INTERIOR_SECTION_LABELS[k],
                          items: allItems.filter(d => (d.section || INTERIOR_SECTIONS.WARM_SHELL) === k && d.included),
                        }))
                        .filter(s => s.items.length > 0);
                      if (sectionsToRender.length === 0) return null;
                      return (
                        <div className="pb-4" style={{ borderBottom: '1px solid var(--brio-border)' }}>
                          <h3 className="font-semibold mb-2" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-primary)' }}>
                            Summary of Interior Deliverables
                          </h3>
                          {sectionsToRender.map(section => (
                            <div key={section.key} className="mb-3">
                              <h4 className="font-semibold mb-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-primary)' }}>
                                {section.label}
                              </h4>
                              <ol className="ml-4 space-y-0.5" style={{ listStyleType: 'decimal', fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}>
                                {section.items.map(d => (
                                  <li key={d.id}>{d.label}</li>
                                ))}
                              </ol>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {activeScope.stages.filter(s => s.selected).length === 0 && (
                      <p className="italic" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-muted)' }}>No stages selected.</p>
                    )}
                    {activeScope.stages.filter(s => s.selected).map(stage => {
                      const allDelivs = [...stage.deliverables.standard, ...stage.deliverables.optional];
                      const included = [
                        ...stage.deliverables.standard.filter(d => d.included),
                        ...stage.deliverables.optional.filter(d => d.included),
                        ...stage.deliverables.custom.filter(d => d.included),
                      ];
                      return (
                        <div key={stage.key} className="pb-4" style={{ borderBottom: '1px solid var(--brio-border)' }}>
                          <h3 className="font-semibold mb-2" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-primary)' }}>
                            {stage.name}
                            {stage.fullName && stage.fullName !== stage.name && (
                              <span className="ml-1 font-normal" style={{ color: 'var(--brio-text-secondary)' }}>— {stage.fullName}</span>
                            )}
                          </h3>
                          {/* Description */}
                          {stage.description && (
                            <p className="leading-relaxed mb-3" style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}>
                              {stage.description}
                            </p>
                          )}
                          {/* Introduction */}
                          {stage.introduction && (
                            <ScopeSowMarkup
                              text={stage.introduction}
                              deliverables={allDelivs}
                              className="leading-relaxed mb-3"
                              style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-primary)', fontWeight: 300 }}
                            />
                          )}
                          {/* Numbered deliverables */}
                          {included.length > 0 && (
                            <ol className="ml-4 space-y-0.5 mb-3" style={{ listStyleType: 'decimal', fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}>
                              {included.map(d => (
                                <li key={d.id}>
                                  {resolveQuantityTokens(d.label, allDelivs)}
                                  {d.type !== 'standard' && (
                                    <span className="ml-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>({d.type})</span>
                                  )}
                                </li>
                              ))}
                            </ol>
                          )}
                          {/* Notes — resolved tokens, no input fields in preview */}
                          {stage.notes && (
                            <ScopeSowMarkup
                              text={resolveNotesTokens(stage.notes, stage)}
                              className="leading-relaxed italic"
                              style={{ fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-text-secondary)' }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </BrioCard>

          </>
        )}

        {/* Cross-project Scope Overview */}
        {allScopes.length > 0 && (() => {
          const handleAllScopesSort = (key) => {
            if (allScopesSortBy === key) setAllScopesSortDir(d => d === 'asc' ? 'desc' : 'asc');
            else { setAllScopesSortBy(key); setAllScopesSortDir('asc'); }
          };
          const sortIndicator = (key) => allScopesSortBy === key ? (allScopesSortDir === 'asc' ? ' ↑' : ' ↓') : '';
          const rows = allScopes.map(s => {
            const proj = activeProjects.find(p => String(p.id) === String(s.projectId));
            return {
              scope: s,
              projectCode: proj?.projectCode || '',
              projectName: proj?.name || `Project ${s.projectId}`,
            };
          });
          const cmp = (a, b) => {
            const dir = allScopesSortDir === 'asc' ? 1 : -1;
            let va, vb;
            switch (allScopesSortBy) {
              case 'projectCode': va = a.projectCode; vb = b.projectCode; break;
              case 'projectName': va = a.projectName; vb = b.projectName; break;
              case 'title':       va = a.scope.title || ''; vb = b.scope.title || ''; break;
              case 'version':     va = Number(a.scope.version) || 0; vb = Number(b.scope.version) || 0; break;
              case 'status':      va = a.scope.status || ''; vb = b.scope.status || ''; break;
              case 'updatedAt':   va = a.scope.updatedAt || ''; vb = b.scope.updatedAt || ''; break;
              default: return 0;
            }
            if (typeof va === 'string') return va.localeCompare(vb, undefined, { sensitivity: 'base' }) * dir;
            if (va < vb) return -1 * dir;
            if (va > vb) return  1 * dir;
            return 0;
          };
          const sortedRows = [...rows].sort(cmp);
          const headerStyle = { fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)', cursor: 'pointer', userSelect: 'none' };
          const headerCls = 'text-left py-2 pr-3 font-semibold uppercase tracking-wide';
          return (
            <BrioCard className="mb-6">
              <div className="p-4">
                <BrioSection title="All Scope of Work Documents">
                  <div className="overflow-x-auto">
                    <table className="w-full" style={{ fontSize: 'var(--brio-font-size-base)' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--brio-border)' }}>
                          <th className={headerCls} style={headerStyle} onClick={() => handleAllScopesSort('projectCode')}>Code{sortIndicator('projectCode')}</th>
                          <th className={headerCls} style={headerStyle} onClick={() => handleAllScopesSort('projectName')}>Project{sortIndicator('projectName')}</th>
                          <th className={headerCls} style={headerStyle} onClick={() => handleAllScopesSort('title')}>Title{sortIndicator('title')}</th>
                          <th className={headerCls} style={headerStyle} onClick={() => handleAllScopesSort('version')}>Version{sortIndicator('version')}</th>
                          <th className={headerCls} style={headerStyle} onClick={() => handleAllScopesSort('status')}>Status{sortIndicator('status')}</th>
                          <th className="text-left py-2 font-semibold uppercase tracking-wide" style={headerStyle} onClick={() => handleAllScopesSort('updatedAt')}>Updated{sortIndicator('updatedAt')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map(({ scope: s, projectCode, projectName }) => (
                          <tr
                            key={s.id}
                            className="cursor-pointer"
                            style={{
                              borderBottom: '1px solid var(--brio-border)',
                              backgroundColor: s.id === activeScopeId ? 'var(--brio-gold-light)' : 'transparent',
                              transition: 'background var(--brio-transition-fast)',
                            }}
                            onClick={() => {
                              if (String(s.projectId) !== String(selectedProjectId)) handleProjectChange(String(s.projectId));
                              setActiveScopeId(s.id);
                            }}
                          >
                            <td className="py-2 pr-3" style={{ color: 'var(--brio-text-primary)', fontWeight: 500 }}>{projectCode || '—'}</td>
                            <td className="py-2 pr-3" style={{ color: 'var(--brio-text-primary)' }}>{projectName}</td>
                            <td className="py-2 pr-3" style={{ color: 'var(--brio-text-secondary)' }}>{s.title}</td>
                            <td className="py-2 pr-3" style={{ color: 'var(--brio-text-muted)' }}>v{s.version}</td>
                            <td className="py-2 pr-3"><StatusBadge status={s.status} /></td>
                            <td className="py-2" style={{ color: 'var(--brio-text-muted)' }}>
                              {new Date(s.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </BrioSection>
              </div>
            </BrioCard>
          );
        })()}

        {/* Bottom spacing */}
        <div style={{ height: 'var(--brio-spacing-xl)' }} />
      </BrioFrame>

      {showImportWizard && (
        <SowImportWizard
          onClose={() => setShowImportWizard(false)}
          onSave={handleImportFromPdf}
          activeProjects={activeProjects}
          confirmedTemplates={confirmedTemplates}
          defaultProjectId={selectedProjectId}
        />
      )}
    </>
  );
}
